import { api } from './api';
import config from 'data/config';
import { hasValue } from 'swipeclock-frontend';

export const masterDataApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMasterDataCompany: build.query({
      query: ({ siteId }) => ({
        url: `${config.masterDataServiceURL}/api/company?&idList=${siteId}&idType=SiteId`,
        method: 'GET',
      }),
      transformResponse: (res) => {
        // Master Data
        if (hasValue(res)) {
          return {
            ...res[0],
          };
        }
        return {
          ...res,
        };
      },
      providesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_COMPANY` }];
      },
    }),
    getEmployeeAgreements: build.query({
      query: ({ masterEmployeeId }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${masterEmployeeId}/agreement?needsConsent=true`,
        method: 'GET',
      }),
      transformResponse: (res) => {
        // res is an array of agreements
        const billingAgreement = 3;
        const messagingPolicy = 4;

        // Filter out agreements that match the billingAgreement or messagingPolicy
        const filteredRes = res.filter(
          (agreement) =>
            agreement.agreementId !== billingAgreement &&
            agreement.agreementId !== messagingPolicy
        );

        return filteredRes;
      },
      providesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_EMPLOYEE_AGREEMENTS` }];
      },
    }),
    getLoginDetails: build.query({
      query: () => ({
        url: `${config.masterDataServiceURL}/api/login/details`,
        method: 'GET',
      }),
      providesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_LOGIN_DETAILS` }];
      },
    }),
    getLoginUrl: build.query({
      query: () => ({
        url: `${config.masterDataServiceURL}/api/company/employeeLogonUrl`,
        method: 'GET',
      }),
      providesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_LOGIN_URL` }];
      },
    }),
    getMasterDataAuthorization: build.query({
      query: () => ({
        url: `${config.masterDataServiceURL}/api/authorization`,
        method: 'GET',
      }),
      // leaving these unused variables to show they are available
      providesTags: (_result, _error, _args) => {
        return [{ type: 'master-data', id: `MASTER_DATA_AUTH` }];
      },
    }),
    getMasterDataEmployee: build.query({
      query: ({ employeeId }) => ({
        url: `${config.masterDataServiceURL}/api/employee?ids=${employeeId}&status=Active&idType=MasterId`,
        method: 'GET',
      }),
      transformResponse: (res) => {
        const employee = res?.[0] ?? {};
        return employee;
      },
      providesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_EMPLOYEE` }];
      },
    }),
    getPartner: build.query({
      query: () => ({
        url: `${config.masterDataServiceURL}/api/partner`,
        method: 'GET',
      }),
      providesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_PARTNER` }];
      },
    }),
    getStateOptions: build.query({
      query: () => ({
        url: `${config.masterDataServiceURL}/api/shared/state`,
        method: 'GET',
      }),
      providesTags: () => [
        { type: 'master-data', id: 'MASTER_DATA_STATE_OPTIONS' },
      ],
    }),
    getTimezoneOptions: build.query({
      query: () => ({
        url: `${config.masterDataServiceURL}/api/shared/timezone`,
        method: 'GET',
      }),
      providesTags: () => [
        { type: 'master-data', id: 'MASTER_DATA_TIMEZONE_OPTIONS' },
      ],
    }),
    verifyEmployeeInvite: build.query({
      query: ({ employeeId, guid }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${employeeId}/invite/${guid}`,
        method: 'GET',
      }),
      // Optionally, provide tags for cache invalidation
      providesTags: () => [{ type: 'master-data', id: 'AUTH_VERIFY' }],
    }),
    verifyPasswordReset: build.query({
      query: ({ employeeId, guid }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${employeeId}/validatePasswordResetLink/${guid}`,
        method: 'GET',
      }),
      // Optionally, provide tags for cache invalidation
      providesTags: () => [{ type: 'master-data', id: 'AUTH_VERIFY_PASSWORD' }],
    }),
    // ########################################################
    // ### Mutations
    // ########################################################
    acceptEmployeeInvite: build.mutation({
      query: ({ employeeId, guid, password }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${employeeId}/invite/${guid}`,
        method: 'POST',
        body: { password },
      }),
      // Optionally, invalidate tags to refetch related data
      invalidatesTags: [{ type: 'master-data', id: 'AUTH_DETAILS' }],
    }),
    resetPassword: build.mutation({
      query: ({ password, employeeId }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${employeeId}/resetPassword`,
        method: 'POST',
        body: { password },
      }),
    }),
    resetPartnerPassword: build.mutation({
      query: ({ password, loginId }) => ({
        url: `${config.masterDataServiceURL}/api/login/${loginId}/password`,
        method: 'POST',
        body: { password },
      }),
    }),
    resetPasswordWithLink: build.mutation({
      query: ({ password, employeeId, guid }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${employeeId}/resetPasswordWithLink/${guid}`,
        method: 'POST',
        body: { password },
      }),
    }),
    saveEmployeeAgreements: build.mutation({
      query: ({ agreements, masterEmployeeId }) => ({
        url: `${config.masterDataServiceURL}/api/employee/${masterEmployeeId}/agreement`,
        method: 'POST',
        body: agreements,
      }),
      invalidatesTags: [
        { type: 'master-data', id: 'MASTER_DATA_EMPLOYEE_AGREEMENTS' },
      ],
    }),
    updateLoginDetails: build.mutation({
      query: ({ loginDetails }) => ({
        url: `${config.masterDataServiceURL}/api/login/details`,
        method: 'POST',
        body: loginDetails,
      }),
      invalidatesTags: () => {
        return [{ type: 'master-data', id: `MASTER_DATA_LOGIN_DETAILS` }];
      },
    }),
  }),
});

export const {
  useGetMasterDataCompanyQuery,
  useGetLoginDetailsQuery,
  useGetLoginUrlQuery,
  useGetEmployeeAgreementsQuery,
  useGetMasterDataAuthorizationQuery,
  useGetMasterDataEmployeeQuery,
  useGetPartnerQuery,
  useGetStateOptionsQuery,
  useGetTimezoneOptionsQuery,
  useVerifyEmployeeInviteQuery,
  useVerifyPasswordResetQuery,
  useAcceptEmployeeInviteMutation,
  useResetPasswordMutation,
  useResetPasswordWithLinkMutation,
  useResetPartnerPasswordMutation,
  useSaveEmployeeAgreementsMutation,
  useUpdateLoginDetailsMutation,
} = masterDataApi;
